import type { TableState } from 'types/game';

/** Stable key — skip duplicate table_state emissions */
export function tableAnimationFingerprint(state: TableState): string {
  const betSum = state.players.reduce((sum, p) => sum + (p.currentBet || 0), 0);
  return [
    state.battleUuid,
    state.roundNumber,
    state.phase,
    state.potAmount,
    state.currentStake,
    state.activeSeat ?? 'x',
    state.activePlayerUserId ?? 'x',
    betSum,
    state.lastTimedOutUserId ?? 'x',
    state.winnerUserId ?? 'x',
  ].join('|');
}

export interface AnimationBaselineRefs {
  prevPot: { current: number };
  prevActiveSeat: { current: number | null };
  bootAnimatedRound: { current: number };
  bootChipRound: { current: number };
  /** Last hand (battleId) that already played the deal animation */
  lastAnimatedHand: { current: number };
  prevTimedOut: { current: number | null };
  wasSeen: { current: boolean };
  collectStarted: { current: boolean };
  prevPhase: { current: string };
}

/** Seed refs from a snapshot — no animations should run for this state */
export function seedAnimationBaseline(
  state: TableState,
  refs: AnimationBaselineRefs,
  _bootAmount: number,
  isSeen: boolean
): void {
  refs.prevPot.current = state.potAmount;
  refs.prevActiveSeat.current = state.activeSeat;
  refs.bootAnimatedRound.current = state.roundNumber;
  refs.bootChipRound.current =
    state.roundNumber > 0 && (state.phase === 'betting' || state.phase === 'dealing') && state.potAmount > 0
      ? state.roundNumber
      : 0;
  refs.lastAnimatedHand.current = state.battleId > 0 ? state.battleId : 0;
  refs.prevTimedOut.current = state.lastTimedOutUserId ?? null;
  refs.wasSeen.current = isSeen;
  /** Skip collect replay on fresh join/rejoin — never replay win animation from snapshot */
  const skipWinReplay =
    state.phase === 'completed' ||
    state.phase === 'waiting' ||
    (state.phase === 'showdown' && state.handStartCountdown != null);
  refs.collectStarted.current = skipWinReplay;
  refs.prevPhase.current = state.phase;
}

/** Re-sync after tab focus — same as seed, clears pending animation triggers */
export function resyncAnimationBaseline(
  state: TableState,
  refs: AnimationBaselineRefs,
  bootAmount: number,
  isSeen: boolean
): void {
  seedAnimationBaseline(state, refs, bootAmount, isSeen);
}
